// src/components/PurchaseFlow/Step/PaymentStep.js
import React, { useEffect, useRef, useState, useContext } from 'react';
import './PaymentStep.css';
import Skeleton from 'react-loading-skeleton';
import NavigationButtons from '../../NavigationButtons';
import 'react-loading-skeleton/dist/skeleton.css';
import api from '../../../../services/api';
import Swal from 'sweetalert2';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';

const PaymentStep = ({ onNext, onBack, clientData, purchaseRequestId, onCardTokenized }) => {
  const { selectedOffer } = useContext(SelectedOfferContext);
  const paymentWidgetRef = useRef(null);

  // Loading states
  const [isWidgetLoading, setIsWidgetLoading] = useState(true);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);

  // Error and final state control
  const [errorMessage, setErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [suggestedCards, setSuggestedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);

  // Trigger to reload the widget if something fails
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const [showWidget, setShowWidget] = useState(false);

  const [isAddingNewCard, setIsAddingNewCard] = useState(false);

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;
  const canSkipPaymentStep = process.env.REACT_APP_ENABLE_SKIP_PAYMENT_STEP === 'true';

  // Check if card suggestions are enabled
  const enabledSections = process.env.REACT_APP_SUGGESTED_DATA_ENABLE?.split(',') || [];
  const showCardSuggestions = enabledSections.includes('card');

  // Agregar nueva variable de entorno
  const allowExpiredCards = process.env.REACT_APP_ALLOW_EXPIRED_CARDS === 'true';

  // Función para calcular la fecha de finalización del préstamo
  const getLoanEndDate = () => {
    if (!selectedOffer?.term) return null;
    
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + selectedOffer.term);
    return endDate;
  };

  // Función para verificar si la tarjeta expira antes del préstamo
  const isCardExpiredBeforeLoan = (card) => {
    if (!selectedOffer?.term || !card?.months_until_expiration) return false;
    
    // Si los meses hasta la expiración son menores que el plazo del préstamo,
    // la tarjeta expirará antes de que termine el préstamo
    return card.months_until_expiration < selectedOffer.term;
  };

  // Función para verificar si el holder_id coincide con el DNI del cliente
  const isCardHolderValid = (card) => {
    if (!clientData?.dni || !card?.holder_id) return false;
    return card.holder_id === clientData.dni;
  };

  // Load suggested cards when component mounts
  useEffect(() => {
    if (showCardSuggestions) {
      loadSuggestedCards();
    } else {
      setShowWidget(true);
    }
  }, []);

  const loadSuggestedCards = async () => {
    if (!showCardSuggestions) return;

    try {
      const response = await api.get(`/customers/${clientData.id}/suggested-data/card`);
      if (response.data) {
        setSuggestedCards(response.data.data);
        if (response.data.data.length === 0) {
          setShowWidget(true);
        }
      }
    } catch (error) {
      console.error('Error loading suggested cards:', error);
      setShowWidget(true);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleUseExistingCard = async (card) => {
    console.log('handleUseExistingCard', card);
    setSelectedCardId(card.id);
    setIsCardSaved(false);
    setIsAddingNewCard(false);
    setShowWidget(false);
    await saveSuggestedCard(card.id);
  };

  const handleUseNewCard = () => {
    setSelectedCardId(null);
    setIsCardSaved(false);
    setShowWidget(true);
    setIsAddingNewCard(true);
  };

  const validateCardData = (cardData) => {
    if (!cardData?.source) {
      setErrorMessage('Datos de tarjeta incompletos. Por favor, inténtalo de nuevo.');
      return false;
    }
    return true;
  };

  /**
   * Sends the card data to the API.
   */
  const sendCardToApi = async (payload) => {
    setIsSavingCard(true);
    try {
      const response = await api.post(`/customers/${clientData.id}/cards`, payload);
      setIsSavingCard(false);

      if (response.status === 201 || response.status === 200) {
        console.log('Card token stored successfully:', response.data);
        if (onCardTokenized) {
          onCardTokenized(response.data);
        }
        setIsCardSaved(true);
        setErrorMessage('');
        return true;
      } else {
        console.error('Unexpected response status:', response.status);
        setErrorMessage('Ocurrió un problema al guardar la tarjeta. Intente nuevamente.');
        return false;
      }
    } catch (error) {
      setIsSavingCard(false);
      console.error('Error storing card token:', error);
      setErrorMessage('No se pudo guardar la tarjeta. Intente nuevamente.');
      return false;
    }
  };

  /**
   * Saves the card token in the backend.
   */
  const saveCardToken = async (cardData) => {
    if (!validateCardData(cardData)) return;

    const { source } = cardData;
    const payload = {
      purchase_request_id: purchaseRequestId,
      token: cardData.token,
      holder: {
        id: source.holder?.identification,
        name: source.holder?.name,
      },
      expiration: source.expiration,
      last_four: source.last_4,
      type: source.type,
      variant: source.variant,
      brand: {
        name: source.brand?.name,
        logo: source.brand?.logo_url
      },
      bank: {
        color: source.bank?.color || '',
        logo: source.bank?.logo_url || '',
        name: source.bank?.name || ''
      }
    };

    await sendCardToApi(payload);
  };

  /**
   * Reloads the widget in case of error.
   * Destroys the current instance and recreates everything from scratch.
   */
  const reloadWidget = () => {
    // Clean up the widget container
    const widgetContainer = document.getElementById('cirenioPaymentContainer');
    if (widgetContainer) {
      widgetContainer.innerHTML = '';
    }

    // Remove the existing script if present
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existingScript) {
      document.body.removeChild(existingScript);
    }

    // Delete the CirenioPayments instance from window
    if (window.CirenioPayments) {
      delete window.CirenioPayments;
    }

    // Reset all states
    setErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(true);
    setIsSavingCard(false);

    // Increase the reloadTrigger to force reloading the script/widget
    setReloadTrigger((prev) => prev + 1);
  };

  /**
   * Loads the Cirenio script and shows the widget.
   * It restarts if reloadTrigger changes (due to a "Retry" action).
   */
  useEffect(() => {
    // Solo cargar el script si debemos mostrar el widget
    if (!showWidget) return;

    // Si no hay sugerencias o el usuario eligió nueva tarjeta
    if (!showCardSuggestions || suggestedCards.length === 0 || (showCardSuggestions && selectedCardId === null)) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;

      script.onload = () => {
        if (window.CirenioPayments) {
          const payments = new window.CirenioPayments(
            publicKey,
            clientData.cuit,
            {
              containerId: 'cirenioPaymentContainer',
              externalId: `pr-${clientData.cuit}-${purchaseRequestId}`
            }
          );

          payments.on('widget_start', () => setIsWidgetLoading(false));

          payments.on('tokenization_success', async (cardData) => {
            // Validar que el holder_id de la tarjeta coincida con el DNI del cliente
            const cardHolderId = cardData?.source?.holder?.identification;
            if (cardHolderId && clientData.dni && cardHolderId !== clientData.dni) {
              // Mostrar modal amigable y recargar el widget
              await Swal.fire({
                html: `
                  <div style="text-align: center; padding: 20px 10px;">
                    <div style="margin-bottom: 30px; display: flex; justify-content: center; align-items: center;">
                      <div style="position: relative; display: inline-block;">
                        <i class="bi bi-credit-card" style="color: #6c757d; font-size: 80px; display: inline;"></i>
                        <i class="bi bi-x-circle-fill" style="color: #dc3545; font-size: 20px; position: absolute; display: inline; top: 12px; right: -6px; background: white; border-radius: 50%;"></i>
                      </div>
                    </div>
                    <h3 style="font-size: 22px; font-weight: 600; color: #333; margin-bottom: 20px;">
                      La tarjeta debe estar a nombre del cliente
                    </h3>
                    <p style="font-size: 16px; margin-bottom: 15px; color: #555;">
                      La tarjeta ingresada no pertenece a <strong>${clientData.firstName} ${clientData.lastName}</strong>.
                    </p>
                    <p style="font-size: 15px; color: #6c757d;">
                      Por favor, solicita al cliente que ingrese una tarjeta de débito que esté a su nombre.
                    </p>
                  </div>
                `,
                confirmButtonText: 'Entendido, intentar nuevamente',
                confirmButtonColor: '#435ebe',
                allowOutsideClick: false,
                showCloseButton: false,
                customClass: {
                  popup: 'swal2-popup-custom',
                  confirmButton: 'btn btn-primary'
                }
              });
              
              // Recargar el widget completamente para permitir un nuevo intento
              reloadWidget();
              return;
            }
            await saveCardToken(cardData);
          });

          payments.on('tokenization_error', () => {
            setErrorMessage('Hubo un problema al procesar tu tarjeta. Por favor, intenta nuevamente.');
          });

          payments.openWidget();
        }
      };

      script.onerror = () => {
        setErrorMessage('No se pudo cargar el widget de pagos. Revisa tu conexión o vuelve a intentarlo.');
      };

      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [
    showWidget,
    scriptSrc,
    publicKey,
    clientData,
    purchaseRequestId,
    reloadTrigger,
    showCardSuggestions,
    suggestedCards.length,
    selectedCardId
  ]);

  const handleContinue = async () => {
    // Si hay una tarjeta seleccionada, procesarla
    if (selectedCardId) {
      setIsSavingCard(true);
      await saveSuggestedCard(selectedCardId);
      setIsSavingCard(false);
      onNext();
      return;
    }

    // Si ya se guardó una tarjeta nueva, pasar al siguiente paso
    if (isCardSaved) {
      onNext();
      return;
    }
  };

  const handleSkipStep = () => {
    // Podríamos mostrar una confirmación si lo consideras necesario
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'No se asociará ninguna tarjeta de débito a esta venta',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar sin tarjeta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#435ebe', // Bootstrap "primary"
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary', // large width btn
        cancelButton: 'btn btn-secondary', // make cancel button large and full width
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onNext();
      }
    });
  };

  /**
   * Renders an alert with the error message.
   * If the error is about loading the script, we show the "vuelve a intentarlo" link inline.
   */
  const renderErrorAlert = () => {
    if (!errorMessage) return null;

    if (errorMessage.includes('No se pudo cargar el widget de pagos')) {
      return (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
          {/* We split the message to insert the clickable link */}
          No se pudo cargar el widget de pagos. Revisa tu conexión o{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textDecoration: 'underline' }}
            onClick={reloadWidget}
          >
            vuelve a intentarlo
          </button>.
        </div>
      );
    }

    // Default error message
    return (
      <div className="alert alert-warning text-center mt-3" role="alert">
        <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
        {errorMessage}
      </div>
    );
  };

  const saveSuggestedCard = async (cardId) => {
    console.log('saveSuggestedCard', cardId);
    const payload = {
      purchase_request_id: purchaseRequestId,
      card_id: cardId
    };
    await sendCardToApi(payload);
  };

  const handleBackToSuggestions = () => {
    setShowWidget(false);
    setIsAddingNewCard(false);
    setSelectedCardId(null);
    setIsCardSaved(false);

    // Limpia el contenedor del widget si existe
    const widget = document.getElementById('cirenioPaymentContainer');
    if (widget) {
      widget.innerHTML = '';
    }
  };

  const handleSelectCard = (card) => {
    if (selectedCardId === card.id) {
      setSelectedCardId(null);
    } else {
      setSelectedCardId(card.id);
    }
  };

  return (
    <div className="container my-5">
      <div className="position-relative mb-4">
        {(isAddingNewCard && suggestedCards.length > 0) && (
          <button 
            className="btn btn-link text-muted position-absolute start-0 top-50 translate-middle-y"
            onClick={handleBackToSuggestions}
            type="button"
          >
            <i className="bi bi-arrow-left me-1"></i>
            Volver a tarjetas guardadas
          </button>
        )}

        <h4 className="text-center mb-0">Asociar Tarjeta de Débito</h4>
        {canSkipPaymentStep && (
          <button 
            className="btn btn-link text-muted position-absolute end-0 top-50 translate-middle-y"
            onClick={handleSkipStep}
            type="button"
          >
            <i className="bi bi-arrow-right me-1"></i>
            Continuar sin tarjeta
          </button>
        )}
      </div>

      <div className="row justify-content-between align-items-start gx-4 mt-5">
        {/* {(!showCardSuggestions && !isLoadingSuggestions) && (

        )} */}

        {/* Estado de carga */}
        {showCardSuggestions && isLoadingSuggestions && (
          <div className="col-12 text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-2 text-muted">Cargando tarjetas existentes...</p>
          </div>
        )}

        {/* Columna del widget de pago o tarjetas existentes */}
        {(!showCardSuggestions || !isLoadingSuggestions) && (
          <div className="row my-0 mt-4">
            {showCardSuggestions && suggestedCards.length > 0 && !isAddingNewCard ? (
              <div className="col-12 suggested-data">
                <h6 className="mb-3 text-muted text-center">Seleccione una tarjeta existente o ingrese una nueva</h6>
                <div className="row g-3">
                  {suggestedCards.map(card => {
                    const isDeactivated = !!card.deactivated_at;
                    const isExpiredBeforeLoan = isCardExpiredBeforeLoan(card);
                    const isHolderValid = isCardHolderValid(card);

                    // Solo deshabilitada si está desactivada, si expira antes y NO está permitido, o si el titular no coincide
                    const isDisabled = isDeactivated || (isExpiredBeforeLoan && !allowExpiredCards) || !isHolderValid;

                    // Si expira antes y está permitido, agregamos warning visual
                    const warningClass = isExpiredBeforeLoan && allowExpiredCards ? ' warning-border' : '';

                    // Color del banco o fallback gris
                    const cardColor = card.bank_color || '#6c757d';

                    return (
                      <div
                        key={card.id}
                        className="col-12 col-sm-6"
                      >
                        <div
                          className={`card-display list-only suggested-card simple${selectedCardId === card.id ? ' selected' : ''}${isDisabled ? ' disabled' : ''}${warningClass}`}
                          style={{ backgroundColor: isDisabled ? '#e0e0e0' : cardColor }}
                          onClick={!isDisabled ? () => handleSelectCard(card) : undefined}
                          title={
                            isDeactivated
                              ? "Esta tarjeta está desactivada"
                              : !isHolderValid
                                ? "El titular de esta tarjeta no coincide con el DNI del cliente"
                                : isExpiredBeforeLoan
                                  ? "Esta tarjeta vence antes del final del préstamo"
                                  : (selectedCardId === card.id ? 'Deseleccionar esta tarjeta' : 'Seleccionar esta tarjeta')
                          }
                          aria-disabled={isDisabled}
                        >
                          {/* Header compacto: logo del banco + últimos 4 dígitos */}
                          <div className="card-header-row">
                            <div className="bank-info">
                              {card.bank_logo ? (
                                <div className="bank-logo-container">
                                  <img src={card.bank_logo} alt={card.bank_name || 'Banco'} />
                                </div>
                              ) : card.bank_name ? (
                                <div className="bank-name">
                                  {card.bank_name}
                                </div>
                              ) : null}
                            </div>
                            <div className="card-last-four">
                              {card.last_four}
                            </div>
                          </div>

                          {/* Contenido compacto: holder + vencimiento + brand */}
                          <div className="card-content">
                            <div className="card-holder-info">
                              <div className="card-holder">
                                {card.holder_name}
                              </div>
                              <div className="card-expiry">
                                {card.expiration_label}
                              </div>
                            </div>
                            
                            {/* Brand en el contenido */}
                            <div className="card-footer-row">
                              {card.brand_logo ? (
                                <div className="brand-logo-container">
                                  <img src={card.brand_logo} alt={card.brand_name} />
                                </div>
                              ) : (
                                <div style={{ fontSize: '8px', opacity: 0.8, maxWidth: '30px', textAlign: 'center' }}>
                                  {card.brand_name}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Labels de estado */}
                          {isDeactivated && (
                            <span className="deactivated-label">
                              Desactivada
                            </span>
                          )}
                          {/* Label si el titular no coincide */}
                          {!isHolderValid && !isDeactivated && (
                            <span className="deactivated-label">
                              Titular no coincide
                            </span>
                          )}
                          {/* Solo muestra el label si está deshabilitada por vencimiento */}
                          {isExpiredBeforeLoan && !allowExpiredCards && !isDeactivated && isHolderValid && (
                            <span className="deactivated-label">
                              Vence antes del préstamo
                            </span>
                          )}
                          {/* Ícono de advertencia si expira antes y está permitida */}
                          {isExpiredBeforeLoan && allowExpiredCards && isHolderValid && (
                            <span className="warning-icon">
                              <i className="bi bi-exclamation-triangle-fill"></i>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="col-12 col-sm-6">
                    <div
                      className="card-display list-only suggested-card simple d-flex align-items-center justify-content-center nueva-tarjeta"
                      onClick={handleUseNewCard}
                      tabIndex={0}
                      role="button"
                      title="Ingresar nueva tarjeta"
                      onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') handleUseNewCard(); }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>＋</span>
                        <span style={{ fontWeight: 500, fontSize: 15 }}>Ingresar nueva tarjeta</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="col-5">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title mb-3">Información Importante</h5>
                      <div className="mb-4">
                        <h6 className="mb-3">
                          <i className="bi bi-credit-card me-2"></i>
                          Requisitos de la Tarjeta
                        </h6>
                        <ul className="list-unstyled">
                          <li className="mb-2">
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            Debe ser una tarjeta de débito
                          </li>
                          <li className="mb-2">
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            Debe estar a tu nombre
                          </li>
                          <li className="mb-2">
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            Debe estar vinculada a la cuenta donde recibís tus ingresos
                          </li>
                        </ul>
                      </div>
                      <div className="mb-4">
                        <h6 className="mb-3">
                          <i className="bi bi-info-circle me-2"></i>
                          ¿Para qué se utilizará?
                        </h6>
                        <p className="text-muted mb-2">
                          Se utilizará como medio alternativo de cobranza y quedará vinculada para futuras compras
                        </p>
                      </div>
                      <div>
                        <h6 className="mb-3">
                          <i className="bi bi-shield-lock me-2"></i>
                          Seguridad
                        </h6>
                        <p className="text-muted mb-0">
                          Tus datos son tratados con los más altos estándares de seguridad bancaria y están protegidos por la ley de protección de datos personales
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-7">
                  <div className="payment-widget-wrapper">
                    {isWidgetLoading && showWidget && <Skeleton height={680} />}
                    <div
                      id="cirenioPaymentContainer"
                      ref={paymentWidgetRef}
                      className="payment-widget"
                      style={{ display: isWidgetLoading || !showWidget ? 'none' : 'block' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {renderErrorAlert()}

      <NavigationButtons
        onBack={onBack}
        onNext={handleContinue}
        disableNext={!selectedCardId && !isCardSaved}
        isLoading={isSavingCard}
        additionalButtons={
          canSkipPaymentStep ? (
            <button 
              className="btn btn-outline-secondary" 
              onClick={handleSkipStep}
              type="button"
            >
              Omitir este paso
            </button>
          ) : null
        }
      />
    </div>
  );
};

export default PaymentStep;

